import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export type UserSettings = {
  forceDarkMode: boolean
  autoFlip: boolean
  autoplayAudio: boolean
}

const STORAGE_KEY = 'perlingo.userSettings'

const readFromStorage = (): Partial<UserSettings> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return {}
    const obj = parsed as Record<string, unknown>
    return {
      forceDarkMode: obj.forceDarkMode === true,
      autoFlip: obj.autoFlip === true,
      autoplayAudio: obj.autoplayAudio === true,
    }
  } catch {
    return {}
  }
}

export const clearAllPerlingoBrowserData = () => {
  try {
    const keys: string[] = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith('perlingo.')) keys.push(k)
    }
    for (const k of keys) window.localStorage.removeItem(k)
  } catch {
    // ignore
  }

  try {
    const keys: string[] = []
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const k = window.sessionStorage.key(i)
      if (k && k.startsWith('perlingo.')) keys.push(k)
    }
    for (const k of keys) window.sessionStorage.removeItem(k)
  } catch {
    // ignore
  }
}

export const useUserSettingsStore = defineStore('userSettings', () => {
  const stored = typeof window !== 'undefined' ? readFromStorage() : {}

  const forceDarkMode = ref(Boolean(stored.forceDarkMode))
  const autoFlip = ref(Boolean(stored.autoFlip))
  const autoplayAudio = ref(Boolean(stored.autoplayAudio))

  const snapshot = computed<UserSettings>(() => ({
    forceDarkMode: forceDarkMode.value,
    autoFlip: autoFlip.value,
    autoplayAudio: autoplayAudio.value,
  }))

  watch(
    snapshot,
    (next) => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // ignore
      }
    },
    { deep: true },
  )

  return {
    forceDarkMode,
    autoFlip,
    autoplayAudio,
  }
})
